import { Component, Input } from '@angular/core';
import { IColumnDef } from '../../../model/table.model';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent<T> {
  @Input() columns: IColumnDef<T>[] = [];
  @Input() tableData: T[] = [];

  currentPage: number = 1;
  pageSize: number = 10;

  sortColumn: IColumnDef<T> | null = null;
  sortDirection: 'asc' | 'desc' | null = null;

  get totalPages(): number {
    return Math.ceil(this.tableData.length / this.pageSize);
  }

  isPercentage(col: IColumnDef<T>): boolean {
    return (col as any).isPercentage === true;
  }

  get sortedData(): T[] {
    if (!this.sortColumn || !this.sortDirection) {
      return this.tableData;
    }
    const sorted = [...this.tableData];
    const accessor = this.sortColumn.accessor;
    const field = this.sortColumn.field;
    const isPercentageColumn = this.isPercentage(this.sortColumn);

    sorted.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (accessor) {
        aValue = accessor(a);
        bValue = accessor(b);
      } else if (field) {
        aValue = a[field];
        bValue = b[field];
      } else {
        return 0;
      }

      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      if (isPercentageColumn) {
        const aNum = this.parsePercentage(aValue);
        const bNum = this.parsePercentage(bValue);

        if (aNum < bNum) {
          return this.sortDirection === 'asc' ? -1 : 1;
        }
        if (aNum > bNum) {
          return this.sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }

  private parsePercentage(value: any): number {
    if (value == null || value === '') {
      return 0;
    }

    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const numStr = value.replace('%', '').trim();
      const num = parseFloat(numStr);
      return isNaN(num) ? 0 : num;
    }

    return 0;
  }

  get paginatedData(): T[] {
    const startIdx = (this.currentPage - 1) * this.pageSize;
    return this.sortedData.slice(startIdx, startIdx + this.pageSize);
  }

  toggleSort(col: IColumnDef<T>): void {
    if (this.sortColumn !== col) {
      this.sortColumn = col;
      this.sortDirection = 'asc';
    } else {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortColumn = null;
        this.sortDirection = null;
      } else {
        this.sortDirection = 'asc';
      }
    }
    this.goToPage(1);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  trackByFn(index: number, item: any): any {
    return index;
  }
}
